// ABOUTME: Session overrides of the catalog's favorite flags, shared by the stars and the favorites strip.
// ABOUTME: The payload marks favorites once; a star toggled since then is reflected here until the next load.

import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

interface CatalogFavoritesValue {
    /** The favorite state of `key`, the toggled one when it was toggled in this session. */
    isFavorite: (key: string, marked: boolean | undefined) => boolean;
    setFavorite: (key: string, favorite: boolean) => void;
}

const CatalogFavoritesContext = createContext<CatalogFavoritesValue | null>(null);

/** A family or series, keyed by its node id: the code changes on a rename, the id never does. */
export const categoryFavoriteKey = (nodeId: string) => `category:${nodeId}`;
export const productFavoriteKey = (id: string) => `product:${id}`;

/**
 * Holds the stars toggled in the catalog, so the favorites strip follows them without reloading
 * the catalog payload, whose `isFavorite` flags are read once.
 */
export function CatalogFavoritesProvider({ children }: { children: ReactNode }) {
    const [overrides, setOverrides] = useState<Record<string, boolean>>({});

    const isFavorite = useCallback(
        (key: string, marked: boolean | undefined) => overrides[key] ?? marked === true,
        [overrides],
    );
    const setFavorite = useCallback(
        (key: string, favorite: boolean) => setOverrides(prev => ({ ...prev, [key]: favorite })),
        [],
    );

    const value = useMemo(() => ({ isFavorite, setFavorite }), [isFavorite, setFavorite]);
    return (
        <CatalogFavoritesContext.Provider value={value}>
            {children}
        </CatalogFavoritesContext.Provider>
    );
}

/** The catalog's favorite overrides, or null outside a catalog that tracks them. */
export function useCatalogFavorites(): CatalogFavoritesValue | null {
    return useContext(CatalogFavoritesContext);
}
